import { useState, useEffect, useRef, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Card, CardBody } from 'reactstrap'
import PDFPreview from "./PDFPreview";
import PDFControls from "./PDFControls";

export default function PDFViewer({ pdfUrl, showBookmarks = true }) {
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.6);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageChanged, setPageChanged] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [matches, setMatches] = useState([]);
  const [workerLoaded, setWorkerLoaded] = useState(false);
  const [activePosition, setActivePosition] = useState(0);
  const memoizedFile = useMemo(() => pdfUrl, [pdfUrl]);
  const textLayerRef = useRef(null);
  const viewerRef = useRef(null);
  const pageRefs = useRef([]);
  const scrollRef = useRef(null);
  const lastScrollTop = useRef(0);
  const [displayedPages, setDisplayedPages] = useState([]);

  const onDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages);
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;

    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;

      if (currentScrollTop > lastScrollTop.current) {
        setActivePosition(1);
      } else if (currentScrollTop < lastScrollTop.current) {
        if (pageNumber === numPages) {
          return
        }
        setActivePosition(0);
      }

      lastScrollTop.current = currentScrollTop;
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [pageNumber]);

  useEffect(() => {
    setDisplayedPages(getDisplayedPages());
  }, [pageNumber, activePosition]);

  const getDisplayedPages = () => {
    let pages = []
    if (numPages === 1) {
      pages = [pageNumber];
    } else {
      if (activePosition === 0) {
        pages = [pageNumber, pageNumber + 1]
      } else {
        if (pageNumber === 1) {
          pages = [1, 2]
        } else if (pageNumber === numPages) {
          pages = [numPages - 1, numPages]
        } else {
          pages = [pageNumber - 1, pageNumber]
        }
      }
    }

    return pages;
  };

  useEffect(() => {
    const loadPdfWorker = async () => {
      try {
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

        setWorkerLoaded(true);
      } catch (error) {
        console.error("Failed to load PDF worker:", error);
      }
    };
    loadPdfWorker();

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    isFullscreen ? setScale(1) : setScale(0.6);
  }, [isFullscreen]);

  useEffect(() => {
    const observerOptions = {
      root: viewerRef.current?.querySelector(".view"),
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      const visiblePages = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      if (visiblePages.length > 0) {
        const pageIndex = parseInt(visiblePages[0].target.getAttribute("data-page-number"));
        if (!isNaN(pageIndex)) {
          setPageNumber(pageIndex);
        }
      }
    }, observerOptions);

    pageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [numPages, scale]);

  if (!workerLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 p-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <Card className="main-card ">
      <CardBody>
        <div className="bg-white d-flex flex-column main" ref={viewerRef}>
          <PDFControls
            pageRefs={pageRefs}
            isFullscreen={isFullscreen}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            numPages={numPages}
            pdfUrl={memoizedFile}
            setScale={setScale}
            scale={scale}
            viewerRef={viewerRef}
            pageChanged={pageChanged}
            setPageChanged={setPageChanged}
            isSearching={isSearching}
            setIsSearching={setIsSearching}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            matches={matches}
            setMatches={setMatches}
            textLayerRef={textLayerRef}
            activePosition={activePosition}
            setActivePosition={setActivePosition}
            displayedPages={displayedPages}
          />

          <div className="mt-4 d-flex flex-column flex-md-row w-100 pdf-container gap-2 p-2">
            <div className={`d-none d-md-block bg-white rounded overflow-auto ${isFullscreen ? 'pdf-height-fs' : 'pdf-height'} preview-pdf`} >
              {
                memoizedFile && <PDFPreview isFullscreen={isFullscreen} pageChanged={pageChanged}
                  setPageChanged={setPageChanged} totalPages={numPages} pdfData={memoizedFile} setCurrentPage={setPageNumber} currentPage={pageNumber} />
              }
            </div>

            <div className={`PDF-view ${isFullscreen ? 'pdf-height-fs' : 'pdf-height'} h-100 w-100`}>
              <div className="scroll p-4" >
                <div className={`${isFullscreen ? 'pdf-height-fs' : 'pdf-height'} view`} ref={scrollRef}>
                  <div >
                    <Document
                      file={memoizedFile}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={
                        <div className="d-flex align-items-center justify-content-center w-100 overflow-hidden">
                          <div className="spinner-border text-primary" role="status"></div>
                        </div>
                      }
                      error={
                        <div className="text-danger text-center">Failed to load PDF document.</div>
                      }
                    >
                      {Array.from(new Array(numPages), (el, index) => (
                        <div
                          key={`wrapper_${index + 1}`}
                          ref={(el) => (pageRefs.current[index] = el)}
                          data-page-number={index + 1}
                          className="position-relative"
                        >
                          <Page
                            renderMode="canvas"
                            pageNumber={index + 1}
                            scale={scale}
                            renderAnnotationLayer={false}
                            width={750}
                            className="mb-4 shadow"
                            onRenderSuccess={() => {
                              const textLayers = document.querySelectorAll(
                                ".react-pdf__Page__textContent",
                              );
                              if (textLayers.length > 0) {
                                textLayerRef.current = textLayers[0];
                              }
                            }}
                          />
                        </div>
                      ))}
                    </Document>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
